<template>
  <div class="content-group">
    <div class="group -flex -xwide">
      <!-- error state -->
      <callout v-if="!fetchDisabled && scaleError" type="error">
        <div>{{ scaleError }}</div>
      </callout>

      <div :class="visible === 'mobile' ? 'mp-mobile meal-plan-actions' : 'mp-desktop'">
        <energy-card
          :initial-calorie-types="initialCalorieTypes"
          :fetch-disabled="fetchDisabled"
          :is-card-link="visible === 'mobile'"
          :show-header="visible === 'desktop'"
          :show-actions="visible === 'desktop' ? 'buttonLink' : 'chevron'"
          from-name="Recipe"
          @energy-loaded="fetchScalePlanDayRecipe"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { useArticlesStore } from '@/stores/articles'
import { useEntityContextStore } from '@/stores/entity-context'
import { fbApi } from '@/utils/http-api'
import Callout from '@/components/notifications/callout.vue'
import EnergyCard from '@/components/users/settings/energy/energy-card.vue'

/**
 * Wrapper around energy-card specific to recipes.
 * - energy-card shows twice on recipe.show.blade.php
 * - one for mobile, one for desktop, and css hides based on breakpoints
 * - some hacky props to make sure only ONE XHR fires
 */
export default defineComponent({
  name: 'RecipeEnergyCard',
  components: {
    EnergyCard,
    Callout,
  },
  props: {
    visible: {
      type: String,
      default: 'mobile',
      validator: (val: string) => ['mobile', 'desktop'].includes(val),
    },

    // if only one card, keep defaults
    // if two cards, fetchDisabled: false and pass in initialCalorieTypes
    // for second card
    fetchDisabled: {
      type: Boolean,
      default: false,
    },
    initialCalorieTypes: {
      type: Object,
      default: () => ({}),
    },
  },

  /* state */
  data() {
    return {}
  },

  /* computed */
  computed: {
    // from Laravel detail view information: id, title, etc
    ...mapState(useUserAuthStore, ['settings']),
    ...mapState(useEntityContextStore, ['entity', 'context']),
    ...mapState(useArticlesStore, ['mode', 'scaleLoading', 'scaleError']),
  },

  /* component methods */
  methods: {
    ...mapActions(useArticlesStore, [
      'scaleRecipe',
      'resetRecipe',
      'setScaleLoading',
      'setScaleError',
      'updateCache',
    ]),

    /**
     * XHR to get the scaled meta data and ingredients
     */
    async fetchScalePlanDayRecipe() {
      // hacky way to make sure this fetch only happens once
      // if two recipe widget components are on the page (like recipe-show.blade.php)
      if (this.fetchDisabled) {
        return
      }

      // context should also be set to plan
      if (
        !this.context ||
        this.context.context !== 'plan' ||
        !this.context.planDayId ||
        !this.context.planDayScheduleId
      ) {
        return
      }

      // check energy setting, only fetch if user has them set... the
      // the energy-card.vue component will fetch this data automatically
      // and if user DOES NOT have any settings set, stick with original
      // recipe

      // disabled to force default energy scaling of Medium
      // if (!this.settings || !this.settings.energy || !this.settings.energy.dailyCalorieType) {
      //   // if no energy settings, revert to original mode / meta data
      //   this.resetRecipe()
      //   return
      // }

      // no double XHR
      if (this.scaleLoading) {
        return
      }

      // xhr resets
      this.setScaleLoading(true)
      this.setScaleError('')

      try {
        const res = await fbApi.get(
          `/articles/${this.entity.id}/scale/plan-day/${this.context.planDayId}/${this.context.planDayScheduleId}`,
        )

        if (!res || !res.body || !res.body.data) {
          throw new Error(`Unable to fetch recipe taxonomy`)
        }

        this.updateCache({
          scaledMetaData: res.body.data.metaData,
          scaledIngredients: res.body.data.ingredients,
          scaleBy: res.body.data.scaleBy,
        })

        // if scaleBy is 0, then nothing was scaled, keep original data
        if (res.body.data.scaleBy !== 0) {
          this.scaleRecipe()
        } else {
          this.resetRecipe()
        }
      } catch (err) {
        // if scaling xhr fails, revert back to the original recipe
        console.error(err)
        this.setScaleError(
          `Unable to scale recipe with meal plan, please refresh the page and try again`,
        )
        this.resetRecipe()
      } finally {
        this.setScaleLoading(false)
      }
    },
  },
})
</script>
